import { useState } from "react";
import type { PublicSessionState, TeamRole } from "../types";
import { TokenDisplay } from "../components/TokenDisplay";

function labelFor(r: TeamRole): string {
  return r === "ir-lead" ? "IR Lead" : r === "comms" ? "Comms" : "SOC";
}

function startErrorMessage(code: string): string {
  switch (code) {
    case "start:no-teams":
      return "At least one team has to join before you can start. Share the session link on this page — open it in another tab or browser and join a team.";
    case "phase:not-lobby":
      return "The game has already started.";
    case "forbidden":
    case "not-im":
      return "Only the IM can start the game.";
    default:
      return `Couldn't start the game (${code}).`;
  }
}

interface Props {
  state: PublicSessionState;
  isIm: boolean;
  sessionToken?: string;
  teamToken?: string;
  onStart: () => Promise<string | undefined>;
}

export function Lobby({ state, isIm, sessionToken, teamToken, onStart }: Props) {
  const [startErr, setStartErr] = useState<string>();
  const [starting, setStarting] = useState(false);
  // Post-join: scenarioName + premise + opening narration come straight off
  // the snapshot for everyone (the IM, players, spectators). Pre-join
  // visitors never reach this view — they're on JoinTeam.
  const allTeams = [
    ...(state.myTeam
      ? [{
          id: state.myTeam.id,
          name: state.myTeam.name,
          playerCount: state.myTeam.players.length,
          pressure: state.myTeam.pressure,
          mine: true,
        }]
      : []),
    ...state.otherTeams.map((t) => ({
      id: t.id,
      name: t.name,
      playerCount: t.playerCount,
      pressure: t.pressure,
      mine: false,
    })),
  ];
  const canStart = allTeams.length > 0;

  return (
    <div className="row">
      <section className="panel grow">
        <h2>Lobby — {state.scenarioName}</h2>
        <div className="lb" style={{ marginTop: 6 }}>
          <div className="head">Team</div>
          <div className="head">Players</div>
          <div className="head">Pressure</div>
          <div className="head">Status</div>
          {allTeams.map((t) => (
            <div className="row-team" key={t.id}>
              <div>
                {t.mine ? <span style={{ color: "var(--color-accent)" }}>★ </span> : null}
                {t.name}
              </div>
              <div>{t.playerCount}</div>
              <div>
                <span className="pressure" role="img" aria-label={`pressure ${t.pressure} of 10`}>
                  <span style={{ width: `${t.pressure * 10}%` }} />
                </span>
              </div>
              <div style={{ color: "var(--color-good)" }}>● ready</div>
            </div>
          ))}
        </div>
        {state.myTeam && state.myTeam.players.length > 0 && (
          <div
            style={{
              marginTop: 16,
              borderTop: "1px solid var(--color-border)",
              paddingTop: 12,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--color-text-muted)",
                marginBottom: 8,
              }}
            >
              <span style={{ color: "var(--color-accent)" }}>{state.myTeam.name}</span> roster
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {state.myTeam.players.map((p) => (
                <div
                  key={p.id}
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    border: "1px solid var(--color-border)",
                    padding: "4px 10px",
                  }}
                >
                  {p.handle}
                  {p.teamRole && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 9.5,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "var(--color-accent)",
                      }}
                    >
                      {labelFor(p.teamRole)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {sessionToken && (state.myTeam || isIm) && (
          <div
            style={{
              marginTop: 18,
              borderTop: "1px solid var(--color-border)",
              paddingTop: 14,
            }}
          >
            <h3 style={{ marginBottom: 10 }}>
              {state.myTeam ? "Invite your teammates" : "Share with players"}
            </h3>
            <div style={{ display: "grid", gap: 12 }}>
              {state.myTeam && teamToken && (
                <TokenDisplay
                  label={`Team token — joins ${state.myTeam.name} directly`}
                  token={teamToken}
                />
              )}
              <TokenDisplay
                label={state.myTeam ? "Session token — joins as a different team" : "Session token — share"}
                token={sessionToken}
              />
              <TokenDisplay
                label="Quick-join link"
                token={`${location.origin}/?session=${encodeURIComponent(sessionToken)}`}
              />
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--color-text-subtle)",
                marginTop: 10,
                fontFamily: "var(--font-mono)",
                lineHeight: 1.55,
              }}
            >
              {state.myTeam
                ? "A teammate joining your team needs the team token. Anyone joining a different team only needs the session."
                : "Players use the session token (or the quick-join link) to enter."}
            </div>
          </div>
        )}
      </section>

      <section className="panel sidebar">
        <h2>Brief</h2>
        {state.scenarioPremise ? (
          <p style={{ fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {state.scenarioPremise}
          </p>
        ) : (
          <p style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--color-text-muted)" }}>
            Your IM is selecting the scenario. Hold tight.
          </p>
        )}
        {state.scenarioOpeningNarration && (
          <p
            style={{
              fontSize: 12,
              lineHeight: 1.55,
              color: "var(--color-text-muted)",
              fontStyle: "italic",
              marginTop: 12,
              borderLeft: "2px solid var(--color-accent)",
              paddingLeft: 10,
              whiteSpace: "pre-wrap",
            }}
          >
            {state.scenarioOpeningNarration}
          </p>
        )}
        <div
          style={{
            marginTop: 14,
            fontSize: 11,
            color: "var(--color-text-muted)",
            fontFamily: "var(--font-mono)",
            borderTop: "1px solid var(--color-border)",
            paddingTop: 10,
          }}
        >
          d20 each turn · 11+ to hit · +3 if procedure is established · 10 turns max
        </div>
        {isIm ? (
          <>
            <button
              className="primary"
              disabled={!canStart || starting}
              onClick={async () => {
                setStartErr(undefined);
                setStarting(true);
                const err = await onStart();
                setStarting(false);
                if (err) setStartErr(startErrorMessage(err));
              }}
              style={{
                width: "100%",
                marginTop: 12,
                opacity: canStart && !starting ? 1 : 0.5,
                cursor: canStart && !starting ? "pointer" : "not-allowed",
              }}
            >
              {starting ? "Starting…" : "▶ Start game"}
            </button>
            {!canStart && (
              <div
                style={{
                  fontSize: 11,
                  color: "var(--color-text-muted)",
                  marginTop: 8,
                  fontFamily: "var(--font-mono)",
                  lineHeight: 1.5,
                }}
              >
                Waiting for at least one team to join — share the session link to invite players.
              </div>
            )}
            {startErr && (
              <div style={{ fontSize: 11.5, color: "var(--color-bad, #d9655c)", marginTop: 8, lineHeight: 1.5 }}>
                {startErr}
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              fontSize: 11,
              color: "var(--color-accent)",
              marginTop: 12,
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.08em",
            }}
          >
            ⏳ waiting for the IM to start
          </div>
        )}
      </section>
    </div>
  );
}
